import { domains } from 'config/env'
import { Logger } from 'logging'
import { createApiClient } from 'lib/Api'

const baseApiAddress = `${domains.SECONDSTEP_API_V4}`
const sitesBase = '/sites'

const logEvent = ({ method, url, data, error }) => {
  const eventProperties = { method }

  if (url) {
    eventProperties.url = url
  }
  if (data) {
    eventProperties.data = data
  }
  if (error) {
    eventProperties.error = error
  }

  Logger.logEvent({
    name: 'Second Step API Error',
    properties: eventProperties,
  })
}

const buildQueryString = params => {
  const searchParams = new URLSearchParams()
  Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle arrays by adding multiple parameters with the same key
        value.forEach(item => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    })

  return searchParams.toString()
}

export const createClient = customAddress => {
  const baseURL = customAddress || baseApiAddress
  const _api = createApiClient(baseURL)

  return {
    baseApiAddress: baseURL,
    getNetSuiteAssetsMe: async () => {
      const url = '/netsuiteassets/me'
      try {
        return await _api.get(url)
      } catch (error) {
        logEvent({ method: 'GET', url, error: error.message })
        throw error
      }
    },
    getNetSuiteAssets: async params => {
      const queryString = buildQueryString(params)
      const url = `/netsuiteassets?${queryString}`
      try {
        return await _api.get(url)
      } catch (error) {
        logEvent({ method: 'GET', url, data: params, error: error.message })
        throw error
      }
    },

    updateNetSuiteAsset: async (assetId, params) => {
      const url = `/netsuiteassets/${assetId}`
      try {
        return await _api.put(url, params)
      } catch (error) {
        logEvent({ method: 'PUT', url, data: params, error: error.message })
        throw error
      }
    },

    getCountries: () => {
      const url = '/location/countries'
      return _api.get(url)
    },

    getRegions: country => {
      const url = `/location/regions?country=${country}`
      return _api.get(url)
    },

    getCitiesWithRegion: (country, region) => {
      const url = `location/cities?country=${country}&region=${region}`
      return _api.get(url)
    },

    getCitiesWithoutRegion: country => {
      const url = `location/cities?country=${country}`
      return _api.get(url)
    },

    getNSCustomersWithRegion: (city, country, region) => {
      const url = `/netsuitecustomers?country=${country}&region=${region}&city=${city}`
      return _api.get(url)
    },

    getNSCustomersWithoutRegion: (city, country) => {
      const url = `netsuitecustomers?country=${country}&city=${city}`
      return _api.get(url)
    },

    getUserProfile: () => {
      const url = '/profile/me'
      return _api.get(url)
    },

    getUserAccess: () => {
      const url = '/access/me'
      return _api.get(url)
    },

    getSites: async () => {
      const url = `${sitesBase}/me?includeFutureLicenses=true`
      try {
        return await _api.get(url)
      } catch (error) {
        logEvent({ method: 'GET', url, error: error.message })
        throw error
      }
    },

    getSiteUsers: async siteId => {
      const url = `${sitesBase}/${siteId}/siteusers`
      try {
        return await _api.get(url)
      } catch (error) {
        logEvent({ method: 'GET', url, data: { siteId }, error: error.message })
        throw error
      }
    },

    createSiteUser: async (siteUser, sendEmail) => {
      const url = `${sitesBase}/${siteUser.siteId}/siteusers?sendEmail=${sendEmail}`
      try {
        return await _api.post(url, siteUser)
      } catch (error) {
        logEvent({ method: 'POST', url, data: siteUser, error: error.message })
        throw error
      }
    },

    createSiteUsers: (siteId, siteUsers, sendEmail) => {
      const url = `${sitesBase}/${siteId}/siteusers?sendEmail=${sendEmail}`
      return _api.patch(url, siteUsers)
    },

    updateSiteUser: siteUser => {
      const url = `${sitesBase}/${siteUser.siteId}/siteusers/${siteUser.id}`
      return _api.put(url, siteUser)
    },

    remindSiteUser: siteUser => {
      const url = `${sitesBase}/${siteUser.siteId}/siteusers/${siteUser.id}/remind`
      return _api.post(url)
    },

    remindSiteUsers: (siteId, emailFilters) => {
      const url = `${sitesBase}/${siteId}/siteusers/remind`
      return _api.post(url, emailFilters)
    },
  }
}

// Default export - creates v4 client
const secondStepApiV4 = createClient()
export default secondStepApiV4
